import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { User } from "@supabase/supabase-js";
import {
  Boxes,
  Building2,
  ChevronDown,
  Inbox,
  LogOut,
  Plus,
  Send,
  Settings,
  Users,
  type LucideIcon,
} from "lucide-react";

import { useActiveClientId } from "@/hooks/useActiveClient";

import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import type { Company } from "@/lib/types";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV: NavItem[] = [
  { to: "/clients", label: "Clientes", icon: Building2 },
  { to: "/invitations", label: "Invitaciones", icon: Inbox },
  { to: "/generate", label: "Nueva invitación", icon: Send },
  { to: "/admin", label: "Ejercicios", icon: Boxes },
  { to: "/team", label: "Equipo", icon: Users },
];

interface AppShellProps {
  user?: User;
  onLogout?: () => Promise<unknown>;
  children: React.ReactNode;
}

export function AppShell({ user, onLogout, children }: AppShellProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-screen flex overflow-hidden">
        <IconRail />
        <div className="flex-1 flex flex-col min-w-0">
          <AppHeader user={user} onLogout={onLogout} />
          <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}

// =================== ICON RAIL ===================
function IconRail() {
  const { location } = useRouterState();
  return (
    <aside className="w-14 shrink-0 border-r border-border bg-card flex flex-col items-center py-3 gap-1">
      {NAV.map((item) => {
        const active =
          location.pathname === item.to ||
          location.pathname.startsWith(item.to + "/");
        const Icon = item.icon;
        return (
          <Tooltip key={item.to}>
            <TooltipTrigger asChild>
              <Link
                to={item.to}
                aria-label={item.label}
                className={cn(
                  "relative flex h-10 w-10 items-center justify-center rounded-md transition-colors",
                  active
                    ? "text-foreground bg-accent"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                )}
              >
                <Icon className="h-[18px] w-[18px]" />
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] bg-primary rounded-r-full" />
                )}
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
          </Tooltip>
        );
      })}
      <div className="mt-auto">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label="Settings"
              disabled
              className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground/50 cursor-not-allowed"
            >
              <Settings className="h-[18px] w-[18px]" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Settings (próximamente)</TooltipContent>
        </Tooltip>
      </div>
    </aside>
  );
}

// =================== TOP HEADER ===================
interface HeaderProps {
  user?: User;
  onLogout?: () => Promise<unknown>;
}

function AppHeader({ user, onLogout }: HeaderProps) {
  return (
    <header className="h-14 shrink-0 border-b border-border bg-card/40 flex items-center px-4 gap-3">
      <ClientPicker />
      {user && (
        <div className="ml-auto flex items-center gap-3">
          <UserMenu user={user} onLogout={onLogout} />
        </div>
      )}
    </header>
  );
}

function ClientPicker() {
  const navigate = useNavigate();
  const { location } = useRouterState();
  const [activeCompanyId, setActiveCompanyId] = useActiveClientId();

  const { data: companies = [] } = useQuery({
    queryKey: ["companies", "for-picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, is_active")
        .eq("is_active", true)
        .order("name")
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Pick<Company, "id" | "name" | "is_active">[];
    },
  });

  // Auto-seleccionar el primer cliente disponible si no hay ninguno activo,
  // o si el activo ya no existe / quedó inactivo. Cada empresa es individual,
  // así que siempre tiene que haber un cliente activo.
  useEffect(() => {
    if (companies.length === 0) return;
    const stillValid = activeCompanyId
      ? companies.some((c) => c.id === activeCompanyId)
      : false;
    if (!stillValid) {
      setActiveCompanyId(companies[0].id);
    }
  }, [companies, activeCompanyId, setActiveCompanyId]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const triggerLabel = activeCompany?.name ?? "Sin cliente";
  const noActive = !activeCompany;

  // Si estoy parado en /clients/$id, picking otro cliente debería navegar
  // al detalle de ése. En cualquier otra página, solo cambia el contexto
  // y nos quedamos donde estábamos.
  const onClientsDetailPage = /^\/clients\/[^/]+/.test(location.pathname);

  function pickClient(id: string) {
    setActiveCompanyId(id);
    if (onClientsDetailPage) {
      navigate({ to: "/clients/$clientId", params: { clientId: id } });
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent transition-colors text-sm max-w-[280px]">
          <div className="h-5 w-5 rounded bg-gradient-to-br from-[hsl(272_91%_70%)] to-[hsl(263_85%_50%)] flex items-center justify-center text-[10px] font-bold shrink-0">
            {noActive ? "?" : initialsOf(activeCompany!.name)}
          </div>
          <span className="font-medium tracking-tight truncate">
            {triggerLabel}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[280px] max-h-[420px] overflow-auto"
      >
        <DropdownMenuLabel>Cambiar de cliente</DropdownMenuLabel>
        {companies.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            Sin clientes activos. Creá uno para empezar.
          </div>
        )}
        {companies.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onSelect={() => pickClient(c.id)}
            className={cn(c.id === activeCompanyId && "bg-accent")}
          >
            <div className="h-4 w-4 rounded bg-secondary border border-border flex items-center justify-center text-[9px] font-bold">
              {initialsOf(c.name)}
            </div>
            <span className="flex-1 truncate">{c.name}</span>
            {c.id === activeCompanyId && (
              <span className="text-[10px] text-muted-foreground">Activo</span>
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate({ to: "/clients" })}>
          <Building2 className="h-3.5 w-3.5" />
          <span>Administrar clientes</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate({ to: "/clients/new" })}>
          <Plus className="h-3.5 w-3.5" />
          <span>Nuevo cliente</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ user, onLogout }: HeaderProps) {
  if (!user) return null;
  const initials = (user.email ?? "?").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent transition-colors">
          <div className="h-7 w-7 rounded-full bg-accent border border-border flex items-center justify-center text-[11px] font-semibold">
            {initials}
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <div className="px-2 py-2">
          <div className="text-xs text-muted-foreground">Sesión activa</div>
          <div className="text-sm font-medium truncate">{user.email}</div>
        </div>
        <DropdownMenuSeparator />
        {onLogout && (
          <DropdownMenuItem onClick={() => onLogout()}>
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// =================== HELPERS ===================
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}
